import { IProjectsResponse } from "Model/Responses/IProjectsResponse";
import { IImportStatusResponse } from "Model/Responses/IImportStatusResponse";
import { IProject } from "Model/Storage/IProject";
import { hasNoElements } from "Utils/Array";
import { Configuration } from "Utils/Configuration";
import { isNotNullOrWhiteSpace, isNullOrWhiteSpace } from "Utils/String";
import {
  asQueryParams, asType, del, get, patch
} from "./Request";

let lastXMonths: number = null;

export async function getProjects(token: string, cookie: string, pageSize = 750): Promise<IProjectsResponse> {
  lastXMonths = lastXMonths ?? (await Configuration.getInstanceAsync()).getLoadLatestXMonths();

  const queryParams = asQueryParams({
    pageSize,
    lastXMonths,
    ...(isNotNullOrWhiteSpace(cookie) ? { cookie } : {})
  });

  const response = await get(`project?${queryParams}`, token);
  const projects = await asType<IProject[]>(response);
  return {
    cookie: response.headers.get(`X-Cookie`),
    hasMore: projects?.length === pageSize,
    pageSize: parseInt(response.headers.get(`X-Page-Size`), 10),
    projects
  };
}

export async function getProjectsById(token: string, idList: string[]): Promise<IProject[]> {
  if (hasNoElements(idList)) {
    return [];
  }

  const response = await get(`project/${idList.join(`,`)}`, token);
  return asType<IProject[]>(response);
}

export async function getProjectsSince(token: string, cookie: string): Promise<IProjectsResponse> {
  if (isNullOrWhiteSpace(cookie)) {
    return { projects: [] };
  }

  const response = await get(`project?cookie=${cookie}`, token);
  const projects = await asType<IProject[]>(response);
  return {
    cookie: response.headers.get(`X-Cookie`),
    projects
  };
}

export async function updateProjects(token: string, projects: IProject[]): Promise<void> {
  await Promise.all(
    projects.map((project) => {
      const clone = { ...project };
      delete clone[`@isFullyLoaded`];
      return patch(`project/${project[`@id`]}`, token, JSON.stringify(clone));
    })
  );
}

export async function deleteProject(token: string, id: string): Promise<void> {
  await del(`project/${id}`, token);
}

export async function importProjects(token: string): Promise<void> {
  await get(`data/import`, token);
}

export async function syncProjects(
  token: string,
  providerId: string,
  providerUserName: string,
  months: number
): Promise<void> {
  await get(`data/sync?userName=${providerUserName}&lastXMonths=${months}`, token, { ProviderId: providerId });
}

export async function importStatus(token: string): Promise<boolean> {
  const response = await asType<IImportStatusResponse>(await get(`data/importstatus`, token));
  return response?.inProgress;
}
